import { enumPaymentState } from '@data-layer/enums/payments';
import { enumIsfState } from '@data-layer/enums/isf';
import { CustomStateModel } from '@app/data-layer/models/common';
import { CustomState } from '@data-layer/enums/common';

export class IsfStatusHelper {
  private static deletedStates: enumIsfState[] = [enumIsfState.CBP_DELETED, enumIsfState.CBP_DELETE_PENDING, enumIsfState.DELETED];

  public static getPointColor(
    state: enumIsfState,
    paymentState: enumPaymentState,
    valid: boolean,
    sentWithoutConsolidator: boolean,
    sentWithoutStuffingLocation: boolean,
  ): string {
    let pointColor: string;
    if (!this.deletedStates.includes(state)) {
      switch (paymentState) {
        case enumPaymentState.SUCCESS:
          if (sentWithoutConsolidator || sentWithoutStuffingLocation) {
            pointColor = 'green';
          }
          break;
        default:
          pointColor = null;
          break;
      }

      if (pointColor) {
        return pointColor;
      }
    }

    switch (state) {
      case enumIsfState.NOT_SENT:
        if (valid) {
          pointColor = 'green';
        }
        if (paymentState === enumPaymentState.PENDING) {
          pointColor = 'orange';
        }
        break;
      case enumIsfState.NO_BILL_ON_FILE:
      case enumIsfState.BILL_TYPE_NOT_MATCHED:
        pointColor = 'orange';
        break;
      default:
        pointColor = null;
        break;
    }

    return pointColor;
  }

  public static getSecondText(
    state: enumIsfState,
    paymentState: enumPaymentState,
    valid: boolean,
    sentWithoutConsolidator: boolean,
    sentWithoutStuffingLocation: boolean,
    showSecondTextForDeleted: boolean,
  ): string {
    //#12147 - commenting out flow interrupting return calls
    // if (paymentState === enumPaymentState.PENDING) {
    //   return 'STATUS.FIRST_TEXT.PENDING_PAYMENT';
    // }
    let secondText: string;
    if (!this.deletedStates.includes(state)) {
      switch (paymentState) {
        case enumPaymentState.SUCCESS:
          if (sentWithoutConsolidator || sentWithoutStuffingLocation) {
            secondText = 'MISSING_CS_OR_LG';
          }
          break;
        default:
          secondText = null;
          break;
      }
    } else {
      if (showSecondTextForDeleted) {
        secondText = paymentState === enumPaymentState.SUCCESS ? 'PAID' : 'DRAFT';
      }
    }

    if (secondText) {
      secondText = `STATUS.HINT_TEXT.${secondText}`;
      return secondText;
    }

    switch (state) {
      case enumIsfState.NOT_SENT:
        if (valid) {
          secondText = 'READY_FOR_SEND';
        }
        break;
      case enumIsfState.BOND_PENDING:
        secondText = 'BOND_PURCHASE_IN_PROGRESS';
        break;
      case enumIsfState.DRAFT_CBY_V_SEND:
      case enumIsfState.VALIDATED_AT_PROVIDER:
      case enumIsfState.PROVIDER_SEND_PENDING:
        secondText = 'AWAITING_RESPONSE';
        break;
      case enumIsfState.UNRECOVERABLE:
        secondText = 'CONTACT_SUPPORT';
        break;
      case enumIsfState.NO_BILL_ON_FILE:
        secondText = 'NO_BILL_ON_FILE';
        break;
      case enumIsfState.BILL_TYPE_NOT_MATCHED:
        secondText = 'BILL_TYPE_NOT_MATCHED';
        break;
      default:
        secondText = null;
        break;
    }

    if (!!secondText) {
      secondText = `STATUS.HINT_TEXT.${secondText}`;
    }

    return secondText;
  }

  public static getFirstTextColor(state: enumIsfState, paymentState: enumPaymentState): string {
    let firstTextColor: string;

    //#12147 - commenting out flow interrupting return calls
    // #10828
    // if (paymentState === enumPaymentState.PENDING) {
    //   firstTextColor = 'grey2';
    //   return firstTextColor;
    // }

    switch (state) {
      case enumIsfState.NOT_SENT:
        firstTextColor = 'grey2';
        break;
      case enumIsfState.DRAFT_CBY_V_SEND:
      case enumIsfState.VALIDATED_AT_PROVIDER:
      case enumIsfState.PROVIDER_SEND_PENDING:
      case enumIsfState.BOND_PENDING:
      case enumIsfState.DRAFT_CBP_PEND:
      case enumIsfState.ACCEPTED_CBP:
        firstTextColor = 'blue';
        break;
      case enumIsfState.NOT_VALID_AT_PROVIDER:
      case enumIsfState.REJECTED_CBP:
      case enumIsfState.UNRECOVERABLE:
      case enumIsfState.CBP_DELETED:
      case enumIsfState.DELETED:
      case enumIsfState.CBP_DELETE_PENDING:
      case enumIsfState.NO_BILL_ON_FILE:
      case enumIsfState.BILL_TYPE_NOT_MATCHED:
        firstTextColor = 'error';
        break;
      case enumIsfState.APPROVED:
        firstTextColor = 'green';
        break;
      default:
        firstTextColor = 'grey2';
        break;
    }

    return firstTextColor;
  }

  public static getFirstText(state: enumIsfState, paymentState: enumPaymentState, customState?: CustomStateModel<CustomState>): string {
    let firstText: string;

    //#12147 - commenting out flow interrupting return calls
    // #9070
    // if (paymentState === enumPaymentState.PENDING) {
    //   return 'STATUS.FIRST_TEXT.DRAFT';
    // }

    switch (state) {
      case enumIsfState.NOT_SENT:
        firstText = 'DRAFT';
        break;
      case enumIsfState.DRAFT_CBY_V_SEND:
      case enumIsfState.VALIDATED_AT_PROVIDER:
      case enumIsfState.PROVIDER_SEND_PENDING:
      case enumIsfState.BOND_PENDING:
        firstText = 'SENT';
        break;
      case enumIsfState.NOT_VALID_AT_PROVIDER:
      case enumIsfState.NO_BILL_ON_FILE:
      case enumIsfState.BILL_TYPE_NOT_MATCHED:
        firstText = 'CORRECTION_NEEDED';
        break;
      case enumIsfState.UNRECOVERABLE:
        firstText = 'SUPPORT_NEEDED';
        break;
      case enumIsfState.DRAFT_CBP_PEND:
        firstText = 'RECEIVED';
        break;
      case enumIsfState.REJECTED_CBP:
        firstText = 'REJECTED';
        break;
      case enumIsfState.ACCEPTED_CBP:
        firstText = 'RECEIVED';
        break;
      case enumIsfState.APPROVED:
        firstText = 'APPROVED';
        break;
      case enumIsfState.CBP_DELETED:
      case enumIsfState.DELETED:
        firstText = 'DELETED';
        break;
      case enumIsfState.CBP_DELETE_PENDING:
        firstText = 'REMOVAL_PENDING';
        break;
      case enumIsfState.CUSTOM:
        if (customState) {
          firstText = this.getIsfCustomState(customState);
          break;
        }
        firstText = 'CUSTOM';
        break;
      default:
        firstText = 'DRAFT';
        break;
    }

    if (!!firstText) {
      firstText = `STATUS.FIRST_TEXT.${firstText}`;
    }

    return firstText;
  }

  private static getIsfCustomState(customState: CustomStateModel<CustomState>): string {
    switch (customState.header) {
      case CustomState.CANCELING:
        return 'CANCELING';
      case CustomState.CANCELED:
        return 'CANCELED';
      case CustomState.REFUNDED:
        return 'REFUNDED';
    }
  }
}
